import React, { useState } from 'react';
import { Box, Button, TextField, Typography, IconButton, Alert } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import axiosInstance from '../axiosConfig';
import API_URL from '../config';

const SecuritySettingsPage = () => {
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const [passwordError, setPasswordError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [isMatching, setIsMatching] = useState(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  React.useEffect(() => {
    setTimeout(() => {
      setPasswordError('');
      setSuccessMessage('');
    }, 5000);
  }, [successMessage, passwordError]);

  const validatePassword = (password) => {
    const minLength = 8;
    const hasNumber = /\d/;
    const hasUppercase = /[A-Z]/;
    const hasLowercase = /[a-z]/;
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/;

    if (password.length < minLength) {
      return 'Das Passwort muss mindestens 8 Zeichen lang sein.';
    }
    if (!hasNumber.test(password)) {
      return 'Das Passwort muss mindestens eine Zahl enthalten.';
    }
    if (!hasUppercase.test(password)) {
      return 'Das Passwort muss mindestens einen Großbuchstaben enthalten.';
    }
    if (!hasLowercase.test(password)) {
      return 'Das Passwort muss mindestens einen Kleinbuchstaben enthalten.';
    }
    if (!hasSpecialChar.test(password)) {
      return 'Das Passwort muss mindestens ein Sonderzeichen enthalten.';
    }
    return '';
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });

    if (e.target.name === 'confirmPassword' || e.target.name === 'newPassword') {
      setIsMatching(passwordData.newPassword === e.target.value);
    }
  };

  const handleSavePassword = () => {
    const { currentPassword, newPassword, confirmPassword } = passwordData;
  
    const newErrors = {
      currentPassword: !currentPassword,
      newPassword: !newPassword,
      confirmPassword: !confirmPassword,
    };
  
    setErrors(newErrors);
  
    if (newErrors.currentPassword || newErrors.newPassword || newErrors.confirmPassword) {
      setPasswordError('Bitte füllen Sie alle Felder aus.');
      return;
    }
  
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }
  
    if (newPassword !== confirmPassword) {
      setPasswordError('Die neuen Passwörter stimmen nicht überein.');
      return;
    }
  
    const token = localStorage.getItem('authToken');
    setIsSavingPassword(true);
    setPasswordError(null); // Fehler zurücksetzen
  
    axiosInstance
      .post(
        '/profile/change-password',
        { currentPassword, newPassword },
        { headers: { Authorization: `${token}` } }
      )
      .then((response) => {
        setIsSavingPassword(false);
        setPasswordError(null);
        setSuccessMessage(response.data.message);
      })
      .catch((error) => {
        setIsSavingPassword(false);
        console.error('Fehler beim Ändern des Passworts:', error);
        setSuccessMessage(null);
  
        if (error.response) {
          switch (error.response.status) {
            case 400:
              setPasswordError(error.response.data.message || 'Fehler: Ungültige Eingabe.');
              break;
            case 404:
              setPasswordError('Benutzer nicht gefunden.');
              break;
            case 500:
              setPasswordError('Serverfehler. Bitte versuchen Sie es später erneut.');
              break;
            default:
              setPasswordError('Ein unbekannter Fehler ist aufgetreten.');
          }
        } else {
          setPasswordError('Netzwerkfehler. Bitte überprüfen Sie Ihre Verbindung.');
        }
      });
  };  

  return (
    <Box sx={{ padding: '2rem', maxWidth: '700px', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <Box sx={{ border: '1px solid #ccc', borderRadius: '8px', mt: 1.4 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50px',
            borderBottom: '2px solid #e0e0e0',
            backgroundColor: '#f9f9f9',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: '#333',
              letterSpacing: '0.5px'
            }}
          >
            Passwort ändern
          </Typography>
        </Box>

        {successMessage && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {successMessage}
          </Alert>
        )}

        {passwordError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {passwordError}
          </Alert>
        )}

        <Box sx= {{ padding: '2rem'}}>
        <TextField
          required
          error={errors.currentPassword}
          label="Aktuelles Passwort"
          name="currentPassword"
          type={showCurrentPassword ? 'text' : 'password'}
          value={passwordData.currentPassword}
          onChange={handlePasswordChange}
          fullWidth
          margin="normal"
          helperText={errors.currentPassword ? 'Dieses Feld ist erforderlich' : ''}
          InputProps={{
            endAdornment: (
              <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end">
                {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
        />

        <TextField
          required
          error={errors.newPassword}
          label="Neues Passwort"
          name="newPassword"
          type={showNewPassword ? 'text' : 'password'}
          value={passwordData.newPassword}
          onChange={handlePasswordChange}
          fullWidth
          margin="normal"
          helperText={errors.newPassword ? 'Dieses Feld ist erforderlich' : ''}
          InputProps={{
            endAdornment: (
              <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                {showNewPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
        />

        <TextField
          required
          error={errors.confirmPassword}
          label="Neues Passwort bestätigen"
          name="confirmPassword"
          type={showConfirmPassword ? 'text' : 'password'}
          value={passwordData.confirmPassword}
          onChange={handlePasswordChange}
          fullWidth
          margin="normal"
          helperText={errors.confirmPassword ? 'Dieses Feld ist erforderlich' : ''}
          InputProps={{
            endAdornment: (
              <>
                {isMatching === null ? null : isMatching ? <CheckCircleIcon sx={{ color: 'green' }} /> : <CancelIcon sx={{ color: 'red' }} />}
                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </>
            ),
          }}
        />

        <Button
          variant="contained"
          size="small"
          color="primary"
          onClick={handleSavePassword}
          disabled={isSavingPassword}
          sx={{
            backgroundColor: 'primary.light',
            color: '#fff',
            mt: 2,
            border: 'none',
            '&:hover': {backgroundColor: 'primary.dark', color: '#fff'} // Etwas dunkler beim Hover
          }}>
          {isSavingPassword ? 'Speichern...' : 'Passwort ändern'}
        </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SecuritySettingsPage;
